import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { APP_NAME } from "../../helpers/constants";

const Privacy = () => {
  return (
    <Page>
      <MainHeader>Privacy Policy</MainHeader>
      <EffectiveDate>Last updated: July 11, 2026</EffectiveDate>

      <Section>
        <Paragraph>
          This Privacy Policy describes how {APP_NAME} (&quot;we,&quot; &quot;us,&quot; or
          &quot;our&quot;), a product of Junoware LLC, collects, uses, stores, and protects
          information when business operators and their authorized representatives use our Business
          Personal Property (BPP) tax audit platform (&quot;Services&quot;). This Policy applies to
          information submitted through our website, waitlist applications, document uploads,
          contact forms, and related workflows.
        </Paragraph>
        <Paragraph>
          Because customers upload sensitive corporate financial records (tax notices and asset
          ledgers), we limit processing to diagnostic and appeal purposes and apply
          industry-standard security controls described below.
        </Paragraph>
      </Section>

      <Section>
        <H2>1. Types of Corporate Data Collected</H2>
        <H3>1.1 Account and Contact Information</H3>
        <Paragraph>We may collect:</Paragraph>
        <List>
          <li>Representative name and title</li>
          <li>Corporate email address</li>
          <li>Business name, business address, and telephone number</li>
          <li>
            Account credentials and authentication tokens managed through our identity provider
          </li>
          <li>
            Optional intake metadata such as estimated equipment values or industry selections
          </li>
        </List>
        <H3>1.2 Financial and Asset Records</H3>
        <Paragraph>We may collect documents and data you upload or provide, including:</Paragraph>
        <List>
          <li>PDFs of local county Business Personal Property assessment notices</li>
          <li>Depreciation schedules and fixed-asset ledgers (PDF, CSV, or supported formats)</li>
          <li>Machinery, equipment, tools, and similar personal-property listings</li>
          <li>
            Historical acquisition costs and related valuation fields embedded in those records
          </li>
        </List>
        <Paragraph>
          We may also collect standard technical data (IP address, browser type, device identifiers,
          and usage logs) to secure the platform and improve reliability. We do not require Social
          Security numbers for core diagnostic workflows.
        </Paragraph>
      </Section>

      <Section>
        <H2>2. Purpose of Data Processing</H2>
        <H3>2.1 Diagnostic and Appeal Preparation</H3>
        <Paragraph>
          Corporate data is parsed and processed exclusively to run tax over-charge diagnostics,
          identify phantom assets and over-assessments, contact you regarding calculated savings,
          and compile evidence-backed appeal packages for county assessors. We also use limited
          operational data to maintain security logs, audit trails, and service reliability.
        </Paragraph>
        <H3>2.2 No Commercial Data-Sharing</H3>
        <Paragraph>
          <strong>
            {APP_NAME} does not sell, rent, lease, or share proprietary corporate asset ledgers or
            business contact details with third-party advertisers, data brokers, or unrelated
            marketing networks.
          </strong>
        </Paragraph>
      </Section>

      <Section>
        <H2>3. Information Shared with County Assessor Offices</H2>
        <Paragraph>
          When you authorize an appeal or related filing, we may disclose to the relevant county
          assessor board (or equivalent local tax authority) only the minimum asset verification
          schedules, receipts, depreciation data, and supporting exhibits reasonably necessary to
          legally substantiate the tax appeal. We do not disclose unrelated corporate materials for
          advertising or secondary commercial purposes.
        </Paragraph>
        <Paragraph>We may also share information with:</Paragraph>
        <List>
          <li>
            Infrastructure and security subprocessors that host, store, or process documents under
            confidentiality obligations
          </li>
          <li>Personnel you authorize within your organization</li>
          <li>Government authorities when required by valid legal process</li>
        </List>
      </Section>

      <Section>
        <H2>4. Corporate Security Protocols</H2>
        <Paragraph>
          Uploaded corporate documents are protected using industry-standard secure transfer
          protocols (SSL/TLS encryption in transit) and AES encryption at rest for stored artifacts
          and databases, together with access controls, logical isolation of intake workloads, and
          administrative safeguards appropriate for institutional financial records.
        </Paragraph>
        <Paragraph>
          No transmission or storage system is completely secure. You are responsible for
          safeguarding credentials and notifying us of suspected unauthorized access.
        </Paragraph>
      </Section>

      <Section>
        <H2>5. Retention</H2>
        <Paragraph>
          We retain information as long as needed to complete active diagnostic and appeal
          workflows, satisfy legal and tax record-keeping obligations, resolve disputes, and enforce
          our agreements. You may request deletion of contact records and eligible documents subject
          to legal and operational retention limits.
        </Paragraph>
      </Section>

      <Section>
        <H2>6. Your Choices</H2>
        <Paragraph>
          You may decline optional fields, though some features may be unavailable. Depending on
          your jurisdiction, you may request access, correction, or deletion of personal contact
          information, subject to applicable law and ongoing audit obligations.{" "}
          <strong>We do not sell personal information.</strong>
        </Paragraph>
      </Section>

      <Section>
        <H2>7. Contact</H2>
        <Paragraph>
          Privacy inquiries may be submitted through our <Link to="/contact">Contact</Link> page or
          privacy@solvtax.com. See our <Link to="/terms">Terms of Use</Link> for licensing,
          contingency fee, and liability terms governing document submissions and appeals.
        </Paragraph>
      </Section>
    </Page>
  );
};

const Page = styled.div`
  color: #0f172a;
  font-size: 0.9375rem;
  line-height: 1.65;
  text-align: left;
  width: min(56rem, 92%);
  max-width: 56rem;
  margin: 3rem auto 2.5rem;
  padding-bottom: 3rem;

  a {
    color: #0d9488;
    text-decoration: underline;
  }
`;

const MainHeader = styled.h1`
  text-align: center;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  color: #0f172a;
`;

const EffectiveDate = styled.p`
  text-align: center;
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0.75rem 0 2rem;
`;

const Section = styled.section`
  margin-bottom: 1.5rem;
`;

const H2 = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 0.75rem;
  color: #0f172a;
  text-align: left;
`;

const H3 = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem;
  color: #0f172a;
  text-align: left;
`;

const Paragraph = styled.p`
  margin: 0.75rem 0;
  color: #334155;
  text-align: left;
`;

const List = styled.ul`
  margin: 0.75rem 0 0.75rem 1.25rem;
  padding-left: 1.25rem;
  color: #334155;
  text-align: left;
  list-style-position: outside;

  li + li {
    margin-top: 0.4rem;
  }
`;

export default Privacy;
